/* eslint-disable react/no-unescaped-entities */
/* eslint-disable max-len */
import React, {useState} from 'react';
import {AppState, DataRow} from '../../App';
import {Table, Tag, Select, DatePicker, Button, Tooltip} from 'antd';
import type {RangeValue} from 'rc-picker/lib/interface';
import moment from 'moment';
import 'country-flag-icons/3x2/flags.css';

import './Tracker.scss';
import Search from 'antd/lib/input/Search';
import {
  BackwardOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {useParams} from 'react-router-dom';

interface TrackerProps {
  state: AppState;
}

export const TOPICS_COLOR_MAP = new Map([
  ['ACA', 'purple'],
  ['AID', 'blue'],
  ['CIV', 'geekblue'],
  ['CUL', 'orange'],
  ['CYB', 'green'],
  ['ECO', 'magenta'],
  ['GOV', 'red'],
  ['HUM', 'gold'],
  ['MIL', 'pink'],
  ['PAR', 'yellow'],
  ['VIS', 'cyan'],
  ['TEC', 'volcano'],
  ['DEF', 'silver'],
]);

// Country code to name mapping
export const COUNTRY_NAMES_MAP = new Map([
  // EU Countries
  ['AT', 'Austria'],
  ['EU', 'European Union'],
  ['CZ', 'Czech Republic'],
  ['HU', 'Hungary'],
  ['PL', 'Poland'],
  ['SK', 'Slovakia'],
  // SEA Countries
  ['ASEAN', 'ASEAN'],
  ['BN', 'Brunei'],
  ['KH', 'Cambodia'],
  ['ID', 'Indonesia'],
  ['LA', 'Laos'],
  ['MY', 'Malaysia'],
  ['MM', 'Myanmar'],
  ['PH', 'Philippines'],
  ['SG', 'Singapore'],
  ['TH', 'Thailand'],
  ['VN', 'Vietnam'],
  ['TL', 'Timor-Leste'],
]);

function generateCitation(dataRow: DataRow): string {
  // Format the date in the desired format
  const formattedDate = dataRow.date.format('DD/MM/YYYY');

  // Trim the event text to 7 words
  const eventExcerpt = dataRow.event.split(' ').slice(0, 10).join(' ');

  // eslint-disable-next-line max-len
  let citation = `Gerstl, A., et al, "${eventExcerpt}...", EUSEAR Tracker by CEIAS, ${formattedDate}`;

  // Append link to data entry
  citation += `, ${window.location.href}/${dataRow.id}`;

  return citation;
}

export default function Tracker({state}: TrackerProps) {
  const [filterEuCountries, setFilterEuCountries] = useState([]);
  const [filterSeaCountries, setFilterSeaCountries] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]);
  const [filterDateRange, setFilterDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
  const [filterSearch, setFilterSearch] = useState('');

  const {id} = useParams();

  const TOPICS_NAMES_MAP = new Map(
      state.issueCodes.map((topic) => [topic.CODE, topic.ISSUE]),
  );

  const filteredData = state.trackerData.filter((data) => {
    if (filterEuCountries.length) {
      const countryMatch = filterEuCountries.some((code) => {
        return data.euCountries.includes(code);
      });
      if (!countryMatch) return false;
    }

    if (filterSeaCountries.length) {
      const countryMatch = filterSeaCountries.some((code) => {
        return data.seaCountries.includes(code);
      });
      if (!countryMatch) return false;
    }

    if (filterTopics.length) {
      const topicMatch = filterTopics.some((code) => {
        return data.topics
            .map((t) => t.toLowerCase())
            .includes(code.toLowerCase());
      });
      if (!topicMatch) return false;
    }

    if (filterDateRange) {
      const [startDate, endDate] = filterDateRange;
      return data.date.isAfter(startDate, 'day') && data.date.isBefore(endDate, 'day');
    }

    if (filterSearch) {
      return data.event.toLowerCase().includes(filterSearch.toLowerCase());
    }

    if (id) {
      return data.id === id;
    }

    return true;
  });

  const copyCitation = (row: DataRow) => {
    const citation = generateCitation(row);
    navigator.clipboard.writeText(citation);
  };

  const copyLink = (row: DataRow) => {
    navigator.clipboard.writeText(window.location.href + '/' + row.id);
  };

  const highlighted = (text: string, search: string) => {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => (
          <span
            key={i}
            style={
              part.toLowerCase() === search.toLowerCase() ?
                {backgroundColor: 'yellow'} :
                {}
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  };

  const trackerColumns = [
    {
      dataIndex: 'event',
      title: () => (
        <div>
          <span>Events</span>
        </div>
      ),
      render: (value: string, row: DataRow, index: number) => {
        return (
          <div>
            <span className="event-date">
              {row.date.format('DD MMMM YYYY')}
            </span>
            <p className="event-text">
              {highlighted(row.event, filterSearch)}
              {row.links.map((link, i) => {
                return (
                  <span key={i}>
                    <a href={link} target="_blank" rel="noreferrer">
                      [ {i + 1} ]
                    </a>
                  </span>
                );
              })}
            </p>
            <div className="event-actions">
              {row.id && (
                <Tooltip title="Copy link">
                  <Button
                    onClick={() => copyLink(row)}
                    icon={<LinkOutlined />}
                    size="small"
                    type="ghost"
                  ></Button>
                </Tooltip>
              )}
              <Tooltip title="Copy as citation">
                <Button
                  onClick={() => copyCitation(row)}
                  icon={<CopyOutlined />}
                  size="small"
                  type="ghost"
                ></Button>
              </Tooltip>
            </div>
            <div className="event-meta">
              <div className="country-group">
                {row.euCountries.map((code) => {
                  return (
                    <Tooltip key={code} title={COUNTRY_NAMES_MAP.get(code) || code}>
                      <div className={`flag:${code} country-icon`} />
                    </Tooltip>
                  );
                })}
                {row.seaCountries.map((code) => {
                  return (
                    <Tooltip key={code} title={COUNTRY_NAMES_MAP.get(code) || code}>
                      <div className={`flag:${code} country-icon`} />
                    </Tooltip>
                  );
                })}
              </div>
              <div className="topics">
                {row.topics.map((code: string, i: number) => {
                  return (
                    <Tag
                      key={i}
                      color={TOPICS_COLOR_MAP.get(code.toUpperCase())}
                    >
                      {TOPICS_NAMES_MAP.get(code.toUpperCase())}
                    </Tag>
                  );
                })}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const handleEuCountryChange = (value: string[]) => {
    setFilterEuCountries(value);
  };

  const handleSeaCountryChange = (value: string[]) => {
    setFilterSeaCountries(value);
  };

  const handleTopicChange = (value: string[]) => {
    setFilterTopics(value);
  };

  const handleDateRangeChange = (dates: [moment.Moment, moment.Moment] | null) => {
    setFilterDateRange(dates);
  };

  const ranges: Record<string, RangeValue<moment.Moment>> = {
    'Last 7 days': [moment().subtract(7, 'd'), moment()],
    'Last 14 days': [moment().subtract(14, 'd'), moment()],
    'Last 30 days': [moment().subtract(30, 'd'), moment()],
    'Last 90 days': [moment().subtract(90, 'd'), moment()],
  };

  return (
    <div className="Tracker">
      <h1>EUSEAR Tracker</h1>
      {id ? (
        <div className="back-to-tracker">
          <Button
            type="primary"
            size="large"
            icon={<BackwardOutlined />}
            onClick={() => {
              window.location.href = '/tracker';
            }}
          >
            Back to Tracker
          </Button>
        </div>
      ) : (
        <>
          <p className="about">
            The EU-Southeast Asia Tracker is a website dedicated to providing comprehensive
            information about the development of the strategic, political, security, economic,
            cultural, academic, and civil society relations between the member countries of the
            Association of the Southeast Asian Nations (ASEAN) and the EU and its five Central
            European member states (Austria, Czechia, Hungary, Poland, and Slovakia) from January
            2024 onwards. This tool is tailored for anyone from the scholarly, policymaking,
            business, and journalistic community working on EU-ASEAN relations, with particular
            interest in the above mentioned actors.
          </p>
          <p className="about">
            Events are listed chronologically, with the latest news listed on the top. Events can
            be filtered by topic, countries, time range, and full-text search. Each entry contains
            one or more links to sources in English and language of the corresponding country.
            Each entry also includes Copy as a link and Copy as a citation buttons, allowing for
            easy referencing in publications and the dissemination of our work. Data entries are
            updated on a monthly basis.
          </p>
          <p className="about">
            Did we miss anything?{' '}
            <a href="mailto:gerstl@ceias.eu">Let us know.</a>
          </p>
          <br />
          <h2>Filter events</h2>
          <div className="filters">
            <div className="filters-row">
              <div className="filter-item">
                <label className="tws-label">EU Countries</label>
                <Select
                  mode="multiple"
                  allowClear
                  showArrow
                  style={{width: '100%'}}
                  placeholder="Please select"
                  onChange={handleEuCountryChange}
                  optionLabelProp="label"
                  tagRender={(props: {value: string}) => {
                    return <div className={`flag:${props.value}`} />;
                  }}
                >
                  {state.countryCodes
                      .filter((c) => c.TYPE === 'EU')
                      .map((c) => c.CODE)
                      .map((code) => {
                        return (
                          <Select.Option key={code} value={code} label={code}>
                            <div className={`flag:${code}`} />
                            <span>{COUNTRY_NAMES_MAP.get(code) || code}</span>
                          </Select.Option>
                        );
                      })}
                </Select>
              </div>
              <div className="filter-item">
                <label className="tws-label">SEA Countries</label>
                <Select
                  mode="multiple"
                  allowClear
                  showArrow
                  style={{width: '100%'}}
                  placeholder="Please select"
                  onChange={handleSeaCountryChange}
                  optionLabelProp="label"
                  tagRender={(props: {value: string}) => {
                    return <div className={`flag:${props.value}`} />;
                  }}
                >
                  {state.countryCodes
                      .filter((c) => c.TYPE === 'SEA')
                      .map((c) => c.CODE)
                      .map((code) => {
                        return (
                          <Select.Option key={code} value={code} label={code}>
                            <div className={`flag:${code}`} />
                            <span>{COUNTRY_NAMES_MAP.get(code) || code}</span>
                          </Select.Option>
                        );
                      })}
                </Select>
              </div>
              <div className="filter-item">
                <label className="tws-label">Topics</label>
                <Select
                  mode="multiple"
                  maxTagCount={2}
                  allowClear
                  showArrow
                  style={{width: '100%'}}
                  placeholder="Please select"
                  onChange={handleTopicChange}
                  optionLabelProp="label"
                  tagRender={(props: {value: string}) => {
                    return (
                      <Tag color={TOPICS_COLOR_MAP.get(props.value)}>
                        {TOPICS_NAMES_MAP.get(props.value)}
                      </Tag>
                    );
                  }}
                >
                  {state.issueCodes.map((issue, i) => {
                    return (
                      <Select.Option key={issue.CODE} value={issue.CODE} label={issue.ISSUE}>
                        <Tag color={TOPICS_COLOR_MAP.get(issue.CODE)}>
                          {issue.ISSUE}
                        </Tag>
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
              <div className="filter-item">
                <label className="tws-label">Date Range</label>
                <DatePicker.RangePicker
                  onChange={handleDateRangeChange}
                  ranges={ranges}
                  style={{width: '100%'}}
                />
              </div>
            </div>
            <div className="search-row">
              <Search
                placeholder="Search events"
                onChange={(e) => setFilterSearch(e.target.value)}
                style={{width: '100%'}}
              />
            </div>
          </div>
        </>
      )}
      <div className="table-wrapper">
        <Table
          columns={trackerColumns}
          dataSource={filteredData}
          className="tracker-table"
        ></Table>
      </div>
    </div>
  );
}
